import { Badge, BadgeLevel, BadgeShape, BadgeSize, Button, ButtonLevel, ButtonSize } from '@vizit/atoms';
import { RadialProgress } from '@vizit/atoms';
import { Skeleton, Text, TextColor, TextSize, TextWeight, Tooltip, TooltipTrigger } from '@vizit/atoms';
import { AlertCircleIcon, LockIcon, PlusIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import type * as React from 'react';
import { Button as AriaButton } from 'react-aria-components';

export interface BenchmarkDetailsHeaderProps {
  /**
   * The benchmark name
   */
  name: string;
  /**
   * The benchmark description, shown under the name when set
   *
   * @optional
   */
  description?: string | null;
  /**
   * The image type the benchmark grades, locked at creation
   */
  scope: 'hero' | 'carousel';
  /**
   * Number of images in the benchmark
   */
  imageCount: number;
  /**
   * Images needed before collections can be scored against the benchmark
   */
  minimumImages: number;
  /**
   * Number of images still processing; shows an inline status while above zero
   *
   * @optional
   * @default 0
   */
  processingCount?: number;
  /**
   * Whether the benchmark is still loading
   *
   * @optional
   * @default false
   */
  isLoading?: boolean;
  /**
   * Called from "Add a product category". The button renders without it but does nothing.
   *
   * @optional
   */
  onAddProductCategory?: () => void;
  /**
   * Controls on the right of the toolbar, e.g. sort and "Upload images"
   *
   * @optional
   */
  actions?: React.ReactNode;
  /**
   * Additional CSS classes for the root element
   *
   * @optional
   */
  className?: string;
}

const SCOPE_LABELS = { hero: 'Hero', carousel: 'Carousel' } as const;

function plural(count: number, noun: string) {
  return `${count} ${count === 1 ? noun : `${noun}s`}`;
}

function ImageTypeChip({ scope }: { scope: BenchmarkDetailsHeaderProps['scope'] }) {
  return (
    <TooltipTrigger delay={200}>
      <AriaButton
        aria-label={`Image type: ${SCOPE_LABELS[scope]}, set at creation`}
        className="cursor-default outline-none data-[focus-visible]:ring-1 data-[focus-visible]:ring-gray-utility-700"
      >
        <Badge level={BadgeLevel.Default} shape={BadgeShape.Box} size={BadgeSize.Sm} className="gap-2">
          <LockIcon size={12} aria-hidden />
          <span className="text-xs">{SCOPE_LABELS[scope]}</span>
        </Badge>
      </AriaButton>
      <Tooltip>Set at creation</Tooltip>
    </TooltipTrigger>
  );
}

/**
 * The top of a benchmark page: its name and description, then a toolbar with the product category control, the
 * locked image type, the image count and, while the set is under the scoring minimum, how many images it still
 * needs. `actions` fills the right of the toolbar.
 */
export function BenchmarkDetailsHeader(props: BenchmarkDetailsHeaderProps) {
  const {
    name,
    description,
    scope,
    imageCount,
    minimumImages,
    processingCount = 0,
    isLoading = false,
    onAddProductCategory,
    actions,
    className,
  } = props;

  if (isLoading) {
    return (
      <output className={cx('flex flex-col gap-6', className)} aria-label="Loading benchmark">
        <Skeleton variant="square" className="h-16 w-1/3" />
        <Skeleton variant="square" className="h-10 w-1/2" />
        <Skeleton variant="square" className="h-16 w-full" />
      </output>
    );
  }

  const imagesNeeded = Math.max(minimumImages - imageCount, 0);

  return (
    <div className={cx('flex flex-col gap-8', className)}>
      <div className="flex flex-col gap-2">
        <Text
          elementType="h1"
          color={TextColor.Primary}
          className="font-serif font-light text-display-sm"
          showEllipsis={false}
        >
          {name}
        </Text>
        {description && (
          <Text size={TextSize.Sm} color={TextColor.Tertiary} showEllipsis={false}>
            {description}
          </Text>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-6">
        <div className="flex flex-wrap items-center gap-8">
          <div className="flex items-center gap-4">
            <Text size={TextSize.Sm} weight={TextWeight.Medium} color={TextColor.Tertiary}>
              Product category
            </Text>
            <Button
              level={ButtonLevel.Secondary}
              size={ButtonSize.Sm}
              iconStart={<PlusIcon size={14} aria-hidden />}
              onPress={onAddProductCategory}
            >
              Add a product category
            </Button>
            {processingCount > 0 && (
              <output className="flex items-center gap-2">
                <RadialProgress isIndeterminate size={8} aria-hidden />
                <Text size={TextSize.Xs} color={TextColor.Tertiary}>
                  Processing {plural(processingCount, 'image')}…
                </Text>
              </output>
            )}
          </div>

          <div className="flex items-center gap-4">
            <Text size={TextSize.Sm} weight={TextWeight.Medium} color={TextColor.Tertiary}>
              Image type
            </Text>
            <ImageTypeChip scope={scope} />
          </div>

          <Text
            size={TextSize.Xs}
            weight={TextWeight.Medium}
            color={TextColor.Tertiary}
            className="font-mono uppercase"
          >
            {plural(imageCount, 'image')}
          </Text>

          {imagesNeeded > 0 && (
            <Badge level={BadgeLevel.Warning} shape={BadgeShape.Box} size={BadgeSize.Sm} className="gap-2">
              <AlertCircleIcon size={12} aria-hidden />
              <span className="text-xs">Needs {imagesNeeded} more to score against</span>
            </Badge>
          )}
        </div>

        {actions && <div className="flex shrink-0 items-center gap-6">{actions}</div>}
      </div>
    </div>
  );
}
